import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { AuthContext } from '../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';

// Icons
import { 
  HiDownload, 
  HiUser, 
  HiCalendar, 
  HiChatAlt2,
  HiViewGrid,
  HiCreditCard
} from 'react-icons/hi';
import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp } from 'react-icons/fa';

/**
 * Helper function to send custom events to Google Analytics.
 * @param {string} category - The category of the event (e.g., 'Social', 'Download').
 * @param {string} action - The specific action taken (e.g., 'Share', 'PDF Download Click').
 * @param {string} label - A descriptive label for the event (e.g., the blog title or social platform).
 */
const trackGAEvent = (category, action, label) => {
  ReactGA.event({
    category: category,
    action: action,
    label: label, 
  });
};

const BlogDetails = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [username, setUsername] = useState('');
  const [contact, setContact] = useState('');

  const fetchBlogDetails = async () => {
    try {
      const { data } = await axiosInstance.get(`/blogs/${id}`);
      setBlog(data);
    } catch (error) {
      console.error('Error fetching blog details:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchBlogDetails();
      // Increment backend view count
      axiosInstance.put(`/blogs/${id}/click`).catch(err => console.error("Failed to increment view count.", err));
    }
  }, [id]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !username.trim()) return;

    // Track comment submission event
    trackGAEvent('Engagement', 'Submit Comment', blog.title);

    try {
      await axiosInstance.post(`/blogs/${id}/comment`, {
        comment: newComment,
        username: username,
        contact: contact,
      });

      fetchBlogDetails(); // Refresh comments
      setNewComment('');
      setUsername('');
      setContact('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Convert YouTube link to embeddable URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.hostname.includes('youtube.com') && parsedUrl.searchParams.get('v')) {
        const videoId = parsedUrl.searchParams.get('v');
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return url;
    } catch {
      return url;
    }
  };

  if (!blog) {
    return <div className="text-center p-10">Loading blog post...</div>;
  }
  
  // Create a plain text description from HTML content for meta tags
  const plainTextDescription = blog.content.replace(/<[^>]*>?/gm, '').substring(0, 160);
  const currentUrl = window.location.href;
  const keywords = `${blog.title.split(' ').join(', ')}, ${blog.category_name}`;

  // --- Structured Data for Google Rich Snippets ---
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    },
    "headline": blog.title,
    "description": plainTextDescription,
    "image": blog.image || "",
    "author": {
      "@type": "Person",
      "name": blog.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Your Website Name", // Replace with your site's name
      "logo": {
        "@type": "ImageObject",
        "url": "https://yourwebsite.com/logo.png" // Replace with your logo URL
      }
    },
    "datePublished": blog.created_at
  };

  return (
    <>
      {/* --- SEO, Social Share, and Structured Data Tags --- */}
      <Helmet>
        <title>{blog.title}</title>
        <meta name="description" content={plainTextDescription} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={plainTextDescription} />
        {blog.image && <meta property="og:image" content={blog.image} />}
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={currentUrl} />
        <meta property="twitter:title" content={blog.title} />
        <meta property="twitter:description" content={plainTextDescription} />
        {blog.image && <meta property="twitter:image" content={blog.image} />}
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
      </Helmet>

      <div className="bg-gray-100 min-h-screen py-10">
        <div className="container mx-auto px-4">
          {/* Main Blog Card */}
          <div className="relative bg-white rounded-lg shadow-xl p-6 md:p-8">
            
            {blog.pdf_drive_link && (
              <div className="absolute top-3 right-3 bg-green-500 text-white text-2xl font-bold px-2 py-1 rounded-md z-10">
                PDF
              </div>
            )}

            {/* Header Section */}
            <div className="mb-6">
              <p className="flex items-center gap-2 text-indigo-600 font-semibold">
                <HiViewGrid /> {blog.category_name || 'General'}
              </p>
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mt-2">{blog.title}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 mt-4">
                <span className="flex items-center gap-2"><HiUser /> {blog.author}</span>
                <span className="flex items-center gap-2"><HiCalendar /> {new Date(blog.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Media Section (Video or Image) */}
            {blog.video_link ? (
              <div className="w-full aspect-video mb-8 rounded-lg overflow-hidden shadow-md">
                <iframe src={getEmbedUrl(blog.video_link)} title={blog.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
              </div>
            ) : blog.image ? (
              <img src={blog.image} alt={blog.title} className="w-full h-auto max-h-[500px] object-cover rounded-lg mb-8 shadow-md" />
            ) : null}

            {/* Blog Content */}
            <div className="prose lg:prose-lg max-w-none mb-8" dangerouslySetInnerHTML={{ __html: blog.content }} />

            {/* PDF Download with Event Tracking */}
            {blog.pdf_drive_link && (
              <a 
                href={blog.pdf_drive_link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition mb-8"
                onClick={() => trackGAEvent('Download', 'PDF Download Click', blog.title)}
              >
                <HiDownload size={20} /> Download Attached PDF
              </a>
            )}
            
            {/* Payment Link with Event Tracking */}
            {blog.pay_link && (
              <div className="bg-gray-50 rounded-lg shadow-inner p-6 md:p-8 my-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Support This Content</h2>
                <a 
                  href={blog.pay_link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-3 bg-amber-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-amber-600 transition shadow-lg transform hover:scale-105"
                  onClick={() => trackGAEvent('Engagement', 'Payment Link Click', blog.title)}
                >
                  <HiCreditCard size={22}/>
                  Proceed to Payment
                </a>
              </div>
            )}

            {/* Form Link with Event Tracking */}
            {blog.form_link && (
              <div className="bg-gray-50 rounded-lg shadow-inner p-6 md:p-8 my-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{blog.form_name}</h2>
                <a 
                  href={blog.form_link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-3 bg-purple-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-purple-600 transition shadow-lg transform hover:scale-105"
                 onClick={() => trackGAEvent('Engagement', 'Form Link Click', blog.form_name)}
                >
                  Click Here 
                </a>
              </div>
            )}

            {/* Social Share with Event Tracking */}
            <div className="flex flex-col md:flex-row items-center justify-between border-t border-b py-4 mt-8">
              <div className="flex items-center gap-4 mt-4 md:mt-0">
                <span className="text-sm font-semibold text-gray-700">Share:</span>
                <a onClick={() => trackGAEvent('Social', 'Share', 'Facebook')} href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-700"><FaFacebook size={24}/></a>
                <a onClick={() => trackGAEvent('Social', 'Share', 'Twitter')} href={`https://twitter.com/intent/tweet?url=${currentUrl}&text=${encodeURIComponent(blog.title)}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-sky-500"><FaTwitter size={24}/></a>
                <a onClick={() => trackGAEvent('Social', 'Share', 'LinkedIn')} href={`https://www.linkedin.com/shareArticle?mini=true&url=${currentUrl}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-800"><FaLinkedin size={24}/></a>
                <a onClick={() => trackGAEvent('Social', 'Share', 'WhatsApp')} href={`https://api.whatsapp.com/send?text=${encodeURIComponent(blog.title + ' ' + currentUrl)}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-green-500"><FaWhatsapp size={24}/></a>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-white rounded-lg shadow-xl p-6 md:p-8 mt-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-gray-800">
              <HiChatAlt2 size={28}/> Comments ({blog.comments?.length || 0})
            </h2>
            <div className="space-y-6">
              {blog.comments && blog.comments.length > 0 ? (
                blog.comments.map((comment) => (
                  <div key={comment.id} className="flex items-start gap-4">
                    <div className="bg-gray-200 p-3 rounded-full">
                      <HiUser className="text-gray-500"/>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{comment.username}</p>
                      <p className="text-gray-700">{comment.comment}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No comments yet. Be the first to comment!</p>
              )}
            </div>
            {/* Comment Form */}
            <form onSubmit={handleCommentSubmit} className="mt-8 space-y-4">
              <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} className="w-full p-3 rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Write your comment..." required></textarea>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full p-3 rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Your Name" required />
              <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition">
                Submit Comment
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetails;